import { computed, readonly, ref } from 'vue';
import { usePB } from '@/composables/database/use-pocket-base.ts';
import type { FeatureFlagsRecord } from '@/types/pocketbase-types.ts';
import type { FeatureFlagKey } from '@/types/feature-flags.ts';

const flags = ref<Partial<Record<FeatureFlagKey, boolean>>>({});
const loaded = ref(false);
let initialization: Promise<void> | undefined;
let subscribed = false;
const changeListeners = new Set<() => void>();

export function initializeFeatureFlags() {
  const pb = usePB();

  async function load(): Promise<void> {
    try {
      const records = await pb.db.collection('feature_flags').getFullList<FeatureFlagsRecord>({ sort: '+key' });
      flags.value = Object.fromEntries(records.map((flag) => [flag.key, flag.enabled === true])) as Partial<Record<FeatureFlagKey, boolean>>;
    } catch {
      // Feature flags fail closed so incomplete deployments cannot expose gated functionality.
      flags.value = {};
    } finally {
      loaded.value = true;
    }
  }

  if (!subscribed) {
    subscribed = true;
    pb.subscribe<FeatureFlagsRecord>('feature_flags', ({ action, record }) => {
      const key = record.key as FeatureFlagKey;
      if (action === 'delete') {
        const next = { ...flags.value };
        delete next[key];
        flags.value = next;
        for (const listener of changeListeners) listener();
        return;
      }
      flags.value = { ...flags.value, [key]: record.enabled === true };
      for (const listener of changeListeners) listener();
    });
  }
  initialization ??= load();

  return { loaded: readonly(loaded), ready: initialization, refresh: load };
}

export function onFeatureFlagsChanged(listener: () => void) {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

export function isFeatureEnabled(key: FeatureFlagKey) {
  return flags.value[key] === true;
}

export function useFeatureFlag(key: FeatureFlagKey) {
  return computed(() => flags.value[key] === true);
}
